import {Icon, type IconName} from '@wandb/components';
import {clsx} from 'clsx';
import React from 'react';
import {Button, Disclosure, DisclosurePanel} from 'react-aria-components';
import {flushSync} from 'react-dom';

import {type AgentChatStepKind} from '../../services/analytics/agentChatEvents';
import {ShimmerText} from './ShimmerText';
import {useTrackWBAgentInspection} from './useTrackWBAgent';
import {useSuppressWBAgentAutoscroll} from './WBAgentAutoscrollContext';
import {WBAgentIcon, type WBAgentIconColor} from './WBAgentIcons';

type WBAgentStepDisclosureProps = React.PropsWithChildren<{
  icon: IconName;
  label: string;
  preview?: string;
  iconColor?: WBAgentIconColor;
  defaultExpanded?: boolean;
  iconSpin?: boolean;
  isRunning?: boolean;
  stepKind: AgentChatStepKind;
  toolName?: string;
  panelClassName?: string;
}>;

export function WBAgentStepDisclosure({
  icon,
  label,
  preview,
  iconColor,
  iconSpin = false,
  defaultExpanded = false,
  isRunning = false,
  stepKind,
  toolName,
  panelClassName,
  children,
}: WBAgentStepDisclosureProps) {
  const {trackStepExpanded, trackStepCollapsed} = useTrackWBAgentInspection();
  const suppressAutoscroll = useSuppressWBAgentAutoscroll();

  return (
    <Disclosure
      defaultExpanded={defaultExpanded}
      onExpandedChange={expanded => {
        // Suppress AUI's resize-triggered autoscroll for an expanded
        // disclosure when a thread is idle. `suppressAutoscroll` no-ops
        // while the thread is running, so we still see pin-to-bottom
        // when agent is streaming its response.
        flushSync(() => suppressAutoscroll());
        if (expanded) {
          trackStepExpanded(stepKind, toolName);
        } else {
          trackStepCollapsed(stepKind, toolName);
        }
      }}
      className="flex flex-col group/disclosure">
      <Button
        slot="trigger"
        className="flex w-full items-center gap-8 px-6 py-4 text-left transition-colors rounded-md bg-transparent hover:bg-wb-surface-hover cursor-pointer text-wb-text-primary">
        <span
          className={clsx(
            'inline-flex shrink-0',
            iconSpin && 'animate-spin [animation-duration:4s]'
          )}>
          <WBAgentIcon icon={icon} size="sm" color={iconColor} />
        </span>
        <span className="text-left leading-[21px] font-semibold text-sm transition-colors">
          {label}
        </span>
        {preview ? (
          <span
            title={preview}
            className="min-w-0 flex-1 truncate text-[13px] leading-[20px] text-wb-text-tertiary">
            <ShimmerText text={preview} isActive={isRunning} />
          </span>
        ) : (
          <span className="flex-1" />
        )}
        <span className="-mr-4 inline-flex shrink-0 rotate-0 text-wb-text-tertiary transition-[rotate] duration-[220ms] group-data-[expanded]/disclosure:rotate-90">
          <Icon icon="chevron-next" size="sm" />
        </span>
      </Button>
      <DisclosurePanel className="h-[--disclosure-panel-height] overflow-clip transition-[height] duration-200">
        <div className={panelClassName ?? 'pb-8 pt-4'}>{children}</div>
      </DisclosurePanel>
    </Disclosure>
  );
}
